import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import Api from "../api/api"
import { ImageField, useImageField } from "./ImageField"
import { buildImageSrcSet } from "./widgets/imageSrcSet"
import Spinner from "./widgets/Spinner"
import {
  initialPlacement, InkBounds, loadLogoSource, LOGO_BAND, LOGO_CANVAS_HEIGHT, LogoPlacement, MIN_LOGO_WIDTH, renderLogoCanvas
} from "./logoFrame"
import { backdropDismiss } from "../utils/backdropDismiss"

export const LOGO_FIELD_HINT = "Transparent PNG. Picking a file opens the placement editor, which fits the logo into a shared frame so all logos carry the same visual weight; replacing an existing logo starts from its artwork again."
export const SKU_LOGO_FIELD_HINT = `Shown in place of the SKU's name. Without one, a single-product SKU shows its product's logo and any other SKU its name as text. ${LOGO_FIELD_HINT}`

// A neighbor in the editor's comparison strip: anything carrying a
// normalized logo (products and skus both do).
export interface LogoSibling {
  id: string
  name: string
  logoUrl?: string
  logoAvailableWidths?: number[]
}

interface LogoField {
  logo: ImageField
  editor: ReactNode
  adjust: (() => void) | undefined
}

// The logo field shared by the product and sku dialogs: an ImageField whose
// picks go through the placement editor (stage/upload stay the plain
// presign→PUT flow). Keeps the decoded source and the last placement so
// Adjust… reopens the editor where the admin left it. Render `editor`
// inside the dialog. fetchSiblings must be stable (useCallback) — it runs
// once, when the editor first opens.
function useLogoField(
  accessToken: string,
  presign: (width: number, entityId: string) => Promise<{ uploadUrl: string, rev: string }>,
  fetchSiblings: () => Promise<LogoSibling[]>
): LogoField {
  const [source, setSource] = useState<{ bitmap: ImageBitmap, ink: InkBounds | null } | null>(null)
  const [placement, setPlacement] = useState<LogoPlacement | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const [siblings, setSiblings] = useState<LogoSibling[] | null>(null)

  const field = useImageField(accessToken, presign, {
    onPick: async (file) => {
      try {
        const loaded = await loadLogoSource(file)
        setSource((prev) => { prev?.bitmap.close(); return loaded })
        setPlacement(undefined)
        setOpen(true)
      } catch {
        field.fail("That file doesn't look like an image.")
      }
    }
  })

  useEffect(() => () => { source?.bitmap.close() }, [source])

  // the comparison strip's neighbors, fetched once the editor first opens
  useEffect(() => {
    if (!open || siblings !== null || !accessToken) return
    let stale = false
    fetchSiblings()
      .then((loaded) => { if (!stale) setSiblings(loaded.filter((s) => s.logoUrl)) })
      .catch(() => { if (!stale) setSiblings([]) })
    return () => { stale = true }
  }, [open, siblings, accessToken, fetchSiblings])

  const logo: ImageField = {
    ...field,
    clear: () => {
      field.clear()
      setSource(null)
      setPlacement(undefined)
    }
  }
  const editor = open && source !== null ? (
    <LogoPlacementDialog
      bitmap={source.bitmap}
      ink={source.ink}
      initial={placement}
      siblings={siblings ?? []}
      onDone={(placed) => {
        setPlacement(placed.placement)
        field.stage(placed.blob, placed.width)
        setOpen(false)
      }}
      onCancel={() => setOpen(false)}
    />
  ) : null
  const adjust = source !== null && field.file !== null ? () => setOpen(true) : undefined
  return { logo, editor, adjust }
}

// The product-logo field for Add/EditProductDialog (presign kind "logo";
// upload() takes the product's id). ownerId scopes the comparison strip
// (Admin flows; "" = self), excludeProductId keeps the product being
// edited out of it.
export function useProductLogoField({ accessToken, ownerId, excludeProductId }: {
  accessToken: string
  ownerId?: string
  excludeProductId?: string
}): LogoField {
  const fetchSiblings = useCallback(async (): Promise<LogoSibling[]> => {
    const { products } = await Api.getProducts({ accessToken, ownerId: ownerId || undefined, count: 100 })
    return products.filter((p) => p.id !== excludeProductId)
  }, [accessToken, ownerId, excludeProductId])
  return useLogoField(
    accessToken,
    (width, productId) => Api.createProductImageUpload({ productId, width, kind: "logo", accessToken }),
    fetchSiblings
  )
}

// The sku-logo field for Add/EditSkuDialog (upload() takes the sku's id).
// ownerId scopes the comparison strip, which shows
// the owner's other sku logos first (own ones only — a borrowed product
// logo would show up twice), then its product logos — a sku's logo sits
// next to both. excludeSkuId keeps the sku being edited out of it.
export function useSkuLogoField({ accessToken, ownerId, excludeSkuId }: {
  accessToken: string
  ownerId?: string
  excludeSkuId?: string
}): LogoField {
  const fetchSiblings = useCallback(async (): Promise<LogoSibling[]> => {
    const [skus, products] = await Promise.all([
      Api.getSkus({ accessToken, sellerId: ownerId || undefined, count: 100 })
        .then((r): LogoSibling[] => r.skus.filter((s) => s.logoProductId === undefined), (): LogoSibling[] => []),
      Api.getProducts({ accessToken, ownerId: ownerId || undefined, count: 100 })
        .then((r): LogoSibling[] => r.products, (): LogoSibling[] => [])
    ])
    return [...skus.filter((s) => s.id !== excludeSkuId), ...products]
  }, [accessToken, ownerId, excludeSkuId])
  return useLogoField(
    accessToken,
    (width, skuId) => Api.createSkuImageUpload({ skuId, width, kind: "logo", accessToken }),
    fetchSiblings
  )
}

// stage size on screen (CSS px); its aspect fixes how many canvas px wide
// the visible frame is
const STAGE_WIDTH = 480
const STAGE_HEIGHT = 180
const FRAME_WIDTH = STAGE_WIDTH / STAGE_HEIGHT * LOGO_CANVAS_HEIGHT   // canvas px
const MIN_SCALE = 0.05
const MAX_SCALE = 16
// beyond this the source is visibly upsampled into the band
const UPSCALE_WARNING = 1.25

export interface PlacedLogo {
  blob: Blob
  width: number
  placement: LogoPlacement
}

// The logo placement editor: a nested modal over the Add/Edit Product and
// Add/Edit SKU dialogs. The admin drags and zooms the source over a frame the height of
// the normalized logo canvas, with the core band (where the main part of
// the logo belongs) and the top/bottom margins (faint stuff only) drawn as
// guides. "Use logo" composites the result (logoFrame.renderLogoCanvas)
// and hands the PNG back; the source and placement are kept by the caller
// so the editor can be reopened to adjust. `siblings` (the same owner's
// other logos) feed the comparison strip.
export default function LogoPlacementDialog({ bitmap, ink, initial, siblings, onDone, onCancel }: {
  bitmap: ImageBitmap
  ink: InkBounds | null
  initial?: LogoPlacement
  siblings: LogoSibling[]
  onDone: (placed: PlacedLogo) => void
  onCancel: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const stageRef = useRef<HTMLCanvasElement>(null)
  const defaultPlacement = useMemo(() => initialPlacement(bitmap, ink, FRAME_WIDTH), [bitmap, ink])
  const [placement, setPlacement] = useState<LogoPlacement>(initial ?? defaultPlacement)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  // the comparison strip shows the CURRENT placement, re-rendered lazily
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewWidth, setPreviewWidth] = useState<number | null>(null)
  const drag = useRef<{ pointerId: number, startX: number, startY: number, origin: LogoPlacement } | null>(null)

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  // the stage draws exactly what the export sees: the source at its
  // placement, then the guides on top
  useEffect(() => {
    const canvas = stageRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    canvas.width = STAGE_WIDTH * dpr
    canvas.height = STAGE_HEIGHT * dpr
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    const unit = STAGE_HEIGHT / LOGO_CANVAS_HEIGHT * dpr   // canvas px → device px
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.imageSmoothingQuality = "high"
    ctx.drawImage(
      bitmap,
      placement.x * unit, placement.y * unit,
      bitmap.width * placement.scale * unit, bitmap.height * placement.scale * unit
    )
    const bandTop = LOGO_BAND.top * canvas.height
    const bandBottom = LOGO_BAND.bottom * canvas.height
    // margins dimmed, band left clear
    ctx.fillStyle = "rgba(0, 0, 0, 0.45)"
    ctx.fillRect(0, 0, canvas.width, bandTop)
    ctx.fillRect(0, bandBottom, canvas.width, canvas.height - bandBottom)
    ctx.strokeStyle = "#00a0b0"
    ctx.lineWidth = 1 * dpr
    ctx.setLineDash([])
    ctx.beginPath()
    ctx.moveTo(0, bandTop + 0.5); ctx.lineTo(canvas.width, bandTop + 0.5)
    ctx.moveTo(0, bandBottom + 0.5); ctx.lineTo(canvas.width, bandBottom + 0.5)
    ctx.stroke()
    ctx.fillStyle = "rgba(228, 228, 228, 0.6)"
    ctx.font = `${9 * dpr}pt sans-serif`
    ctx.textBaseline = "top"
    ctx.fillText("margin — shadows, swooshes, faint strokes only", 8 * dpr, 6 * dpr)
    ctx.textBaseline = "bottom"
    ctx.fillText("margin — anything below the frame is cut off", 8 * dpr, canvas.height - 6 * dpr)
    ctx.textBaseline = "middle"
    ctx.textAlign = "right"
    ctx.fillStyle = "rgba(0, 160, 176, 0.7)"
    ctx.fillText("main part of the logo", canvas.width - 8 * dpr, (bandTop + bandBottom) / 2)
  }, [bitmap, placement])

  // comparison strip: debounce the (getImageData-heavy) export while dragging
  useEffect(() => {
    let cancelled = false
    const timer = window.setTimeout(async () => {
      try {
        const { blob, width } = await renderLogoCanvas(bitmap, placement)
        if (cancelled) return
        setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob) })
        setPreviewWidth(width)
      } catch {
        if (!cancelled) { setPreviewUrl(null); setPreviewWidth(null) }
      }
    }, 150)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [bitmap, placement])
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }, [previewUrl])

  // canvas px per rendered CSS px (the stage shrinks with max-width on
  // narrow screens, so never assume STAGE_* on screen)
  const canvasUnit = (canvas: HTMLCanvasElement) => LOGO_CANVAS_HEIGHT / canvas.getBoundingClientRect().height

  // zoom about a point (canvas coordinates), so the pixel under the cursor
  // / the frame center stays put
  const zoomAt = useCallback((factor: number, cx: number, cy: number) => {
    setPlacement((p) => {
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, p.scale * factor))
      const f = scale / p.scale
      return { scale, x: cx - (cx - p.x) * f, y: cy - (cy - p.y) * f }
    })
  }, [])

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (busy) return
    e.currentTarget.setPointerCapture(e.pointerId)
    drag.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, origin: placement }
  }
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drag.current
    if (!d || d.pointerId !== e.pointerId) return
    const unit = canvasUnit(e.currentTarget)
    setPlacement({ ...d.origin, x: d.origin.x + (e.clientX - d.startX) * unit, y: d.origin.y + (e.clientY - d.startY) * unit })
  }
  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (drag.current?.pointerId === e.pointerId) drag.current = null
  }
  // wheel must be a non-passive native listener to preventDefault (React's
  // onWheel is passive) — otherwise the dialog scrolls
  useEffect(() => {
    const canvas = stageRef.current
    if (!canvas) return
    const handler = (e: WheelEvent) => {
      e.preventDefault()
      const rect = canvas.getBoundingClientRect()
      const unit = LOGO_CANVAS_HEIGHT / rect.height
      zoomAt(Math.exp(-e.deltaY * 0.002), (e.clientX - rect.left) * unit, (e.clientY - rect.top) * unit)
    }
    canvas.addEventListener("wheel", handler, { passive: false })
    return () => canvas.removeEventListener("wheel", handler)
  }, [zoomAt])

  const handleUse = async () => {
    setBusy(true)
    setError("")
    try {
      const { blob, width } = await renderLogoCanvas(bitmap, placement)
      if (width < MIN_LOGO_WIDTH)
        throw new Error(`The result is only ${width}px wide — logos must be at least ${MIN_LOGO_WIDTH}px wide. Enlarge it, or use a wider artwork.`)
      onDone({ blob, width, placement })
      dialogRef.current?.close()
    } catch (err) {
      setError(err instanceof Error ? err.message : "unknown error")
      setBusy(false)
    }
  }

  const comparison = siblings.filter((p) => p.logoUrl).slice(0, 2)
  const upscale = placement.scale
  // the zoom slider is logarithmic
  const sliderValue = Math.log(placement.scale)

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog logo-placement-dialog"
      // the native close event doesn't bubble, but React's synthetic one
      // walks up the tree and would fire the HOST dialog's onClose too
      // (closing the Add/Edit dialog) whenever this event lands before the
      // host has unmounted us — stop it here
      onClose={(e) => { e.stopPropagation(); onCancel() }}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Place the logo</h2>
        <p className="generate-codes-subtitle">
          Drag to move, scroll or use the slider to resize. Fit the main part
          of the logo between the lines; thin or faint elements (swooshes,
          shadows, taglines) may extend into the margins, and anything outside
          the frame is cut off. Left/right whitespace is trimmed automatically.
        </p>

        <canvas
          ref={stageRef}
          className="logo-placement-stage"
          style={{ width: STAGE_WIDTH, aspectRatio: `${STAGE_WIDTH} / ${STAGE_HEIGHT}`, cursor: busy ? "default" : "grab" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />

        <div className="logo-placement-controls">
          <span className="generate-codes-label">Size</span>
          <input
            type="range"
            min={Math.log(MIN_SCALE)}
            max={Math.log(MAX_SCALE)}
            step={0.01}
            value={sliderValue}
            onChange={(e) => zoomAt(Math.exp(Number(e.target.value)) / placement.scale, FRAME_WIDTH / 2, LOGO_CANVAS_HEIGHT / 2)}
            disabled={busy}
            style={{ flex: 1 }}
          />
          <button
            className="card-link"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" }}
            onClick={() => setPlacement(defaultPlacement)}
            disabled={busy}
          >
            Reset
          </button>
        </div>
        <span className="generate-codes-hint" style={{ display: "block", textAlign: "left", minHeight: "1.3em" }}>
          {upscale > UPSCALE_WARNING
            ? `This artwork is being upscaled ×${upscale.toFixed(1)} — use a larger file for crisper output if you have one.`
            : previewWidth !== null && previewWidth < MIN_LOGO_WIDTH
              ? `The result is only ${previewWidth}px wide — logos must be at least ${MIN_LOGO_WIDTH}px.`
              : previewWidth !== null ? `Result: ${previewWidth} × ${LOGO_CANVAS_HEIGHT}px` : ""}
        </span>

        {/* how it will look: the real title height, between the org's other logos */}
        <div className="logo-placement-compare">
          {comparison.length > 0 ? (
            <>
              <img
                src={comparison[0].logoUrl}
                srcSet={buildImageSrcSet(comparison[0].logoUrl!, comparison[0].logoAvailableWidths)}
                sizes="200px"
                alt={comparison[0].name}
                className="product-logo-title"
              />
              {previewUrl && <img src={previewUrl} alt="" className="product-logo-title" />}
              {comparison[1] && (
                <img
                  src={comparison[1].logoUrl}
                  srcSet={buildImageSrcSet(comparison[1].logoUrl!, comparison[1].logoAvailableWidths)}
                  sizes="200px"
                  alt={comparison[1].name}
                  className="product-logo-title"
                />
              )}
            </>
          ) : previewUrl ? (
            <img src={previewUrl} alt="" className="product-logo-title" />
          ) : null}
        </div>

        {error && <p className="generate-codes-error">{error}</p>}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
          <button className="btn-upgrade" disabled={busy} onClick={handleUse}>
            {busy ? <><Spinner size={12} /> Rendering…</> : "Use logo"}
          </button>
        </div>
      </div>
    </dialog>
  )
}
